import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CloseIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Center,
  Divider,
  Flex,
  FormControl,
  IconButton,
  Input,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { cloneDeep, remove } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useReduxDispatch } from '../../../app/store';
import { ArcButton } from '../../../common/components/ArcButton';
import { SEHomeCard } from '../../../common/components/SEHomeCard';
import { TimeSelectDrawer } from '../../../common/components/TimeSelectDrawer';
import { WEEKDAYS, WEEKDAYS_TO_API_VALUES } from '../../../common/constants';
import { setIsLoaded } from '../dashboard/evChargerSlice';
import { formatScheduleTime, validateSchedule } from './scheduler-helper';
import {
  addEvChargerSchedule,
  editEvChargerSchedule,
  selectEvChargerSchedules,
  setSchedulerState,
} from './schedulerSlice';

type Props = {
  siteDeviceId: string;
};

type State = {
  name: string;
  daysOfWeek: number[];
  startTime: string;
  endTime: string;
  errorMessage: string;
};

const INITIAL_STATE: State = {
  name: '',
  daysOfWeek: [],
  startTime: '',
  endTime: '',
  errorMessage: '',
};

/* istanbul ignore next -- @preserve */
export function EditSchedule({ siteDeviceId }: Props) {
  const dispatch = useReduxDispatch();
  const { t } = useTranslation();
  const [{ name, daysOfWeek, startTime, endTime, errorMessage }, setState] = useState(INITIAL_STATE);
  const { isSaving, schedules, timeEditType } = useSelector(selectEvChargerSchedules);
  const schedule = useMemo(() => schedules.find((schedule) => schedule.isEditing), [schedules]);
  const isNew = schedule?.groupId === null;
  const toast = useToast();

  useEffect(() => {
    setState({
      name: schedule?.name || '',
      daysOfWeek: schedule?.daysOfWeek || [],
      startTime: schedule?.startTime || '',
      endTime: schedule?.endTime || '',
      errorMessage: '',
    });
  }, [schedule]);

  const bottomElement = useRef<HTMLDivElement>(null);
  const scrollToBottom = () => {
    bottomElement.current?.scrollIntoView({ behavior: 'smooth' });
  };
  useEffect(() => {
    scrollToBottom(); // Scroll to bottom to put focus on the form.
  }, [siteDeviceId, errorMessage]);

  const {
    isOpen: isTimeEditDrawerOpen,
    onClose: onCloseTimeEditDrawer,
    onOpen: onOpenTimeEditDrawer,
  } = useDisclosure();

  function getSelectedTime() {
    if (timeEditType === 'start') {
      return startTime;
    } else if (timeEditType === 'end') {
      return endTime;
    }
    return undefined;
  }

  function handleSaveTime(time: string) {
    if (timeEditType === 'start') {
      setState((prevState) => ({ ...prevState, startTime: time }));
    } else if (timeEditType === 'end') {
      setState((prevState) => ({ ...prevState, endTime: time }));
    }
  }

  function handleDeleteUnsavedSchedule() {
    const clonedSchedules = cloneDeep(schedules);

    if (isNew) {
      remove(clonedSchedules, (schedule) => schedule.isEditing);
    } else {
      const editedSchedule = clonedSchedules.find((schedule) => schedule.isEditing);
      if (editedSchedule) {
        editedSchedule.isEditing = false;
      }
    }

    dispatch(setSchedulerState({ schedules: clonedSchedules }));
  }

  async function handleSaveSchedule() {
    const result = validateSchedule({ name, daysOfWeek, startTime, endTime }, schedules);
    if (result.type === 'INVALID') {
      setState((prevState) => ({ ...prevState, errorMessage: result.message }));
      return;
    }

    setState((prevState) => ({ ...prevState, errorMessage: '' }));

    let showToast = false;
    if (isNew) {
      const resultAction = await dispatch(
        addEvChargerSchedule({ siteDeviceId, newSchedule: { name, daysOfWeek, startTime, endTime } })
      );
      showToast = addEvChargerSchedule.rejected.match(resultAction);
    } else {
      const resultAction = await dispatch(
        editEvChargerSchedule({
          siteDeviceId,
          editedSchedule: { groupId: schedule!.groupId!, name, daysOfWeek, startTime, endTime },
        })
      );
      showToast = editEvChargerSchedule.rejected.match(resultAction);
    }

    if (showToast) {
      toast({
        title: t('Common.something went wrong'),
        description: `${t('EVSE.schedule overlapping error')} ${t('Common.if this persists contact support')}`,
        status: 'error',
        isClosable: true,
      });
    } else {
      dispatch(setIsLoaded(false)); // This will trigger a refetch of the EV charger state.
    }
  }

  if (!schedule) return null;

  return (
    <>
      <SEHomeCard mb={3} borderTopLeftRadius={5}>
        <IconButton
          onClick={handleDeleteUnsavedSchedule}
          bg="none"
          width="100%"
          justifyContent="flex-end"
          aria-label="Cancel"
          icon={<CloseIcon boxSize={3} />}
          data-testid="cancel-add-schedule-button"
          height="100%"
          pb={2}
        />

        <FormControl>
          <Input
            placeholder={t('EVSE.name your schedule')}
            value={name}
            onChange={(e) => setState((prevState) => ({ ...prevState, name: e.target.value }))}
            borderRadius={0}
            background="#fefefe"
            _dark={{ background: 'customGrey.900' }}
            size="lg"
          />
        </FormControl>

        <Divider my={3} borderColor="rgba(151, 151, 151, 0.30)" />

        <Flex justify={'space-between'} overflow="auto">
          {WEEKDAYS.map((weekday, weekdayIndex, arr) => {
            const weekdayValue = WEEKDAYS_TO_API_VALUES[weekday];
            const isWeekdaySelected = daysOfWeek.includes(weekdayValue);
            const isLastDay = weekdayIndex === arr.length - 1;

            return (
              <Box
                key={weekdayIndex}
                as={'button'}
                onClick={() => {
                  if (isWeekdaySelected) {
                    // Unselect the day if selected.
                    setState((prevState) => ({
                      ...prevState,
                      daysOfWeek: prevState.daysOfWeek.filter((day) => day !== weekdayValue),
                    }));
                  } else {
                    setState((prevState) => ({ ...prevState, daysOfWeek: [...prevState.daysOfWeek, weekdayValue] }));
                  }
                }}
                data-is-selected={isWeekdaySelected ? true : undefined}
                pos="relative"
                mr={isLastDay ? 0 : 1}
              >
                <Box
                  pos="relative"
                  w="35px"
                  h="35px"
                  bg={isWeekdaySelected ? 'primaryBranding.200' : 'customGrey.400'}
                  borderRadius="100%"
                />
                <Center pos="absolute" top={0} w="35px" h="35px" fontSize="sm">
                  <Box color={'black'} _light={{ color: 'white' }}>
                    {t(`Common.${weekday.toLowerCase()}`)[0]}
                  </Box>
                </Center>
              </Box>
            );
          })}
        </Flex>

        <Divider my={3} borderColor="rgba(151, 151, 151, 0.30)" />

        <Flex align="center" justify="space-between">
          <Text>{t('Common.start time')}</Text>

          <Button
            onClick={() => {
              dispatch(setSchedulerState({ timeEditType: 'start' }));
              onOpenTimeEditDrawer();
            }}
            p={5}
            fontWeight={400}
            color="#fff"
            bg={'#6F6F76'}
            _light={{ color: '#000', bg: '#ddd' }}
            width="40%"
            whiteSpace={'normal'}
            wordBreak={'break-word'}
          >
            {startTime ? formatScheduleTime(startTime) : t('Common.edit').toUpperCase()}
          </Button>
        </Flex>
        <Divider my={3} borderColor="rgba(151, 151, 151, 0.30)" />

        <Flex align="center" justify="space-between">
          <Text>{t('Common.end time')}</Text>

          <Button
            onClick={() => {
              dispatch(setSchedulerState({ timeEditType: 'end' }));
              onOpenTimeEditDrawer();
            }}
            p={5}
            size="md"
            fontWeight={400}
            color="#fff"
            bg={'#6F6F76'}
            _light={{ color: '#000', bg: '#ddd' }}
            width="40%"
            whiteSpace={'normal'}
            wordBreak={'break-word'}
          >
            {endTime ? formatScheduleTime(endTime) : t('Common.edit').toUpperCase()}
          </Button>
        </Flex>

        <Center mt={4}>
          <ArcButton onClick={handleSaveSchedule} isLoading={isSaving} w={'80%'} arcColor="#3DCD57">
            {t('Common.save')}
          </ArcButton>
        </Center>
        <Box ref={bottomElement} />
      </SEHomeCard>

      <TimeSelectDrawer
        isOpen={isTimeEditDrawerOpen}
        onClose={onCloseTimeEditDrawer}
        onSave={handleSaveTime}
        selectedTime={getSelectedTime()}
      />

      {!!errorMessage && (
        <Text mb={2} color={'red'}>
          {t('Common.edit')}: {t(`EVSE.scheduleValidation.${errorMessage}`)}
        </Text>
      )}
    </>
  );
}
